import { useSyncExternalStore } from 'react';

/**
 * A visitor's chosen storefront ACCENT color override, persisted to localStorage.
 * `null` = use the store's own brand color. Applied in StorefrontLayout via
 * deriveStoreTheme so the whole storefront re-skins live. Mirrors the mobile app's
 * userBrand override (themeStore).
 */
const KEY = 'sf-accent';
const HEX = /^#[0-9a-fA-F]{6}$/;

function readInitial(): string | null {
  try {
    const v = window.localStorage.getItem(KEY);
    return v && HEX.test(v) ? v : null;
  } catch {
    return null;
  }
}

let accent: string | null = readInitial();
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function setStoreAccent(hex: string | null): void {
  accent = hex && HEX.test(hex) ? hex : null;
  try {
    if (accent) window.localStorage.setItem(KEY, accent);
    else window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l());
}

/** The visitor's accent override (null → the store's brand). Re-renders on change. */
export function useStoreAccent(): string | null {
  return useSyncExternalStore(subscribe, () => accent, () => accent);
}

/** Curated accent presets shown in the storefront appearance menu (visitor-picked
 *  colours, not brand design tokens — the token-hex lint rule doesn't apply). */
// eslint-disable-next-line no-restricted-syntax -- user-selectable palette, intentional literals
export const SF_ACCENTS = ['#2D4FE8', '#0EA5E9', '#7C3AED', '#DB2777', '#E11D48', '#EA580C', '#16A34A', '#0D9488'] as const;
